import React, { useState, useEffect } from 'react';
import { auth, functions } from '../firebaseConfig';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import logoXCoin from '../assets/logo_plateado.png';
import background from '../assets/backhome.png';

const VerifyEmailView = () => {
    const { userData } = useUser();
    const navigate = useNavigate();
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    // Auto-redirección si el contexto detecta que ya está verificado
    useEffect(() => {
        if (userData?.emailVerified) {
            navigate('/');
        }
    }, [userData, navigate]);

    const handleResendEmail = async () => {
        setSending(true);
        setMessage('');
        setError('');
        try {
            if (auth.currentUser) {
                // Usar correo personalizado de XCOIN
                const sendVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
                try {
                    await sendVerificationEmail();
                    setMessage('Correo de verificación reenviado exitosamente.');
                } catch (emailError) {
                    console.warn('Error con correo personalizado, usando fallback:', emailError);
                    // Fallback al método genérico si falla
                    await sendEmailVerification(auth.currentUser);
                    setMessage('Correo de verificación reenviado.');
                }
            } else {
                setError('No hay sesión activa.');
            }
        } catch (err) {
            console.error("Error reenviando verificación:", err);
            if (err.code === 'auth/too-many-requests') {
                setError('Has solicitado demasiados reenvíos. Por favor espera unos minutos.');
            } else {
                setError('Error al reenviar el correo. Inténtalo más tarde.');
            }
        } finally {
            setSending(false);
        }
    };

    const handleCheckVerification = async () => {
        try {
            setSending(true); // Usamos estado de carga visual
            await auth.currentUser.reload(); // Obliga a refrescar los datos del usuario desde Firebase
            const freshUser = auth.currentUser;

            if (freshUser.emailVerified) {
                // Forzamos actualización del token para que los claims (si hubiera) se refresquen
                await freshUser.getIdToken(true);
                window.location.reload(); // Recarga completa para reiniciar el UserContext
            } else {
                setError('Aún no detectamos la verificación. Por favor espera unos segundos e intenta nuevamente.');
            }
        } catch (error) {
            console.error("Error verificando:", error);
            setError('Ocurrió un error al verificar. Intenta recargar la página.');
        } finally {
            setSending(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error("Error al cerrar sesión", error);
        }
    };

    const containerStyle = {
        display: 'flex',
        minHeight: '100vh',
        width: '100vw',
        backgroundImage: `url(${background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff'
    };

    const cardStyle = {
        background: 'rgba(23, 23, 23, 0.95)', // Dark background similar to dashboard
        padding: '2rem',
        borderRadius: '15px',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        maxWidth: '500px',
        width: '90%',
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    };

    return (
        <div style={containerStyle}>
            <div style={cardStyle}>
                <img src={logoXCoin} alt="XCoin Logo" style={{ width: '200px', marginBottom: '2rem' }} />

                <h2 style={{ marginBottom: '1rem', color: '#fff' }}>Verifica tu Correo</h2>

                <p style={{ marginBottom: '1rem', color: '#ccc', lineHeight: '1.6' }}>
                    Te hemos enviado un enlace de confirmación a: <br />
                    <strong style={{ color: '#00D54B', fontSize: '1.1em' }}>{auth.currentUser?.email}</strong>
                </p>

                <p style={{ marginBottom: '2rem', color: '#aaa', fontSize: '0.9em' }}>
                    Para proteger tu seguridad, necesitas verificar tu correo electrónico antes de acceder al Dashboard.
                    Por favor revisa tu bandeja de entrada (y la carpeta de spam).
                </p>

                {message && (
                    <div className="alert alert-success" style={{
                        padding: '10px',
                        marginBottom: '15px',
                        borderRadius: '5px',
                        backgroundColor: 'rgba(0, 213, 75, 0.1)',
                        color: '#00D54B',
                        border: '1px solid #00D54B'
                    }}>
                        {message}
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger" style={{
                        padding: '10px',
                        marginBottom: '15px',
                        borderRadius: '5px',
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                        color: '#dc3545',
                        border: '1px solid #dc3545'
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button
                        onClick={handleCheckVerification}
                        className="btn btn-primary"
                        style={{
                            background: 'linear-gradient(90deg, #00D54B 0%, #00b340 100%)',
                            border: 'none',
                            padding: '12px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            color: '#000',
                            cursor: 'pointer'
                        }}
                    >
                        Ya verifiqué mi correo
                    </button>

                    <button
                        onClick={handleResendEmail}
                        disabled={sending}
                        style={{
                            background: 'transparent',
                            border: '1px solid #00D54B',
                            padding: '12px',
                            borderRadius: '8px',
                            color: '#00D54B',
                            cursor: sending ? 'wait' : 'pointer'
                        }}
                    >
                        {sending ? 'Reenviando...' : 'Reenviar correo de verificación'}
                    </button>

                    <button
                        onClick={handleLogout}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '10px',
                            color: '#6c757d',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            marginTop: '10px'
                        }}
                    >
                        Volver al inicio de sesión
                    </button>
                </div>

                <div style={{ marginTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                    <p style={{ fontSize: '0.8em', color: '#666', marginBottom: '10px' }}>
                        ¿Te registraste con el correo equivocado o quieres empezar de nuevo?
                    </p>
                    <button
                        onClick={async () => {
                            if (window.confirm('¿Estás seguro de que quieres eliminar esta cuenta y empezar de cero? Esta acción no se puede deshacer.')) {
                                try {
                                    setSending(true);
                                    // Importar functions dinámicamente o usar la importada si ya existe
                                    const { functions } = await import('../firebaseConfig');
                                    const { httpsCallable } = await import('firebase/functions');
                                    const eliminarCuenta = httpsCallable(functions, 'eliminarCuentaUsuario');

                                    await eliminarCuenta();
                                    await signOut(auth);
                                    navigate('/register');
                                } catch (err) {
                                    console.error("Error eliminando cuenta:", err);
                                    setError('Error al eliminar la cuenta. Inténtalo de nuevo.');
                                    setSending(false);
                                }
                            }
                        }}
                        style={{
                            background: 'rgba(220, 53, 69, 0.1)',
                            border: '1px solid #dc3545',
                            padding: '8px 15px',
                            borderRadius: '5px',
                            color: '#dc3545',
                            fontSize: '0.9em',
                            cursor: 'pointer'
                        }}
                    >
                        Eliminar cuenta y registrarse de nuevo
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmailView;
